import { useBranch } from "../contexts/BranchContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flame, MapPin, Phone, Star, Quote } from "lucide-react";
import { Separator } from "../components/ui/separator";
import { branchGalleryUrl } from "../utils/branchGallery";

const GETIR_HERO_IMAGE =
  "https://cdn.getiryemek.com/restaurants/1741085357040_1125x522.webp";

const branchImages = {
  caddebostan: {
    hero: GETIR_HERO_IMAGE,
    main: branchGalleryUrl("caddebostan", 4),
    interior: branchGalleryUrl("caddebostan", 2),
  },
  zeytinburnu: {
    hero: GETIR_HERO_IMAGE,
    main: branchGalleryUrl("zeytinburnu", 1),
    interior: branchGalleryUrl("zeytinburnu", 2),
  },
};

const videoSources = [
  "/assets/videos/video1.mp4",
  "/assets/videos/video2.mp4",
  "/assets/videos/video3.mp4",
  "/assets/videos/video4.mp4",
];

const branchLabels = { caddebostan: "Caddebostan", zeytinburnu: "Zeytinburnu" };

const fakeReviews = [
  { name: "Mehmet Y.", rating: 5, text: "İstanbul'da yediğim en iyi et burada. Kuzu pirzola muhteşemdi, servis de çok ilgili. Kesinlikle tekrar geleceğiz!", time: "2 hafta", avatar: "MY" },
  { name: "Ayşe K.", rating: 5, text: "Ailecek gittik, harika bir akşam geçirdik. Etler gerçekten çok taze ve lezzetli. Ambiyans da çok güzeldi.", time: "1 ay", avatar: "AK" },
  { name: "Ali R.", rating: 5, text: "Tepsi kebabı efsane! Porsiyonlar doyurucu, fiyatlar makul. Kahramanmaraş usulü et pişirme sanatını burada yaşadık.", time: "3 hafta", avatar: "AR" },
  { name: "Fatma S.", rating: 4, text: "Zeytinburnu şubesine gittik. Lahmacun ve kebaplar çok güzeldi. Personel güler yüzlü, ortam sıcak ve samimi.", time: "1 ay", avatar: "FS" },
  { name: "Emre D.", rating: 5, text: "Arkadaş grubuyla geldik, ocakbaşı deneyimi muhteşemdi. Adana kebap ve kuşbaşı favorimiz oldu. Herkese tavsiye ederim.", time: "2 hafta", avatar: "ED" },
  { name: "Zeynep T.", rating: 5, text: "Caddebostan şubesi çok şık bir mekan. Etlerin kalitesi her zaman aynı seviyede. En sevdiğim restoran!", time: "3 hafta", avatar: "ZT" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function HomePage() {
  const { selectedBranch } = useBranch();
  const { theme } = useTheme();
  const { t } = useLang();
  const dk = theme === "dark";
  const imgs = branchImages[selectedBranch];

  const bg = dk ? "bg-[#0a0a0a]" : "bg-[#FAFAF8]";
  const bg2 = dk ? "bg-[#121212]" : "bg-[#F0EDEA]";
  const textH = dk ? "text-white" : "text-[#1A1A1A]";
  const textP = dk ? "text-[#ccc]" : "text-[#555]";
  const textM = dk ? "text-[#999]" : "text-[#777]";
  const cardBg = dk ? "bg-[#121212]" : "bg-white";
  const borderS = dk ? "border-white/5" : "border-black/5";

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[85vh] md:h-screen overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={imgs.hero} alt="Tepsi Et" className="w-full h-full object-cover brightness-[0.20]" />
          {/* Üst ve alt aynı yoğunlukta koyu kaplama */}
          <div className={`absolute inset-0 ${dk ? "bg-[#0a0a0a]/93" : "bg-black/91"}`} />
        </div>
        <div className="relative z-10 h-full flex flex-col justify-end pb-20 md:pb-32 max-w-[1400px] mx-auto px-6">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0} className="mb-4">
            <span className="font-body text-xs uppercase tracking-[0.3em] text-[#8B1A1A]">{branchLabels[selectedBranch]} {t.hero.branch}</span>
          </motion.div>
          <motion.h1 initial="hidden" animate="visible" variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-7xl xl:text-8xl font-bold text-white leading-[0.95] mb-6">
            {t.hero.title1} <br /><span className="text-[#8B1A1A] italic">{t.hero.title2}</span>
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={2} className="font-body text-base md:text-lg text-[#ccc] max-w-lg mb-10 leading-relaxed">
            {t.hero.desc}
          </motion.p>
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="flex gap-4">
            <Link to="/menu" data-testid="hero-menu-btn" className="inline-flex items-center gap-2 border border-[#8B1A1A] px-8 py-3.5 text-sm font-body uppercase tracking-[0.2em] text-[#8B1A1A] hover:bg-[#8B1A1A] hover:text-white transition-all duration-300">{t.hero.menuBtn}</Link>
            <Link to="/galeri" data-testid="hero-gallery-btn" className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-body uppercase tracking-[0.2em] text-white/80 hover:text-[#8B1A1A] transition-all duration-300">{t.hero.galleryBtn} <ArrowRight className="w-4 h-4" /></Link>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="overflow-hidden bg-[#8B1A1A] py-3">
        <motion.div animate={{ x: ["0%", "-50%"] }} transition={{ repeat: Infinity, duration: 20, ease: "linear" }} className="flex whitespace-nowrap">
          {Array(8).fill(null).map((_, i) => (
            <span key={i} className="font-heading text-sm md:text-base uppercase tracking-[0.3em] text-white mx-8">
              {t.marquee}
            </span>
          ))}
        </motion.div>
      </div>

      {/* Atmosfer */}
      <section className={`py-20 md:py-32 ${bg}`} data-testid="atmosphere-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="relative overflow-hidden">
              <img src={imgs.main} alt={`Tepsi Et ${branchLabels[selectedBranch]}`} className="w-full aspect-[4/3] object-cover" />
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
              <span className="font-body text-xs uppercase tracking-[0.3em] text-[#8B1A1A] block mb-4">{t.atmosphere.label}</span>
              <h2 className={`font-heading text-3xl sm:text-4xl lg:text-5xl ${textH} mb-6 leading-tight`}>
                {t.atmosphere.title1} <br /><span className="italic text-[#8B1A1A]">{t.atmosphere.title2}</span>
              </h2>
              <p className={`font-body text-base ${textP} leading-relaxed mb-8`}>
                {t.atmosphere.desc}
              </p>
              <ul className="space-y-3 mb-8">
                {t.atmosphere.features.map((item, i) => (
                  <li key={i} className={`flex items-center gap-3 font-body text-sm ${textP}`}>
                    <span className="w-1.5 h-1.5 bg-[#8B1A1A] rounded-full" />{item}
                  </li>
                ))}
              </ul>
              <Link to="/hakkimizda" data-testid="about-link" className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.2em] text-[#8B1A1A] hover:text-[#A52525] transition-colors">
                {t.atmosphere.more} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <Separator className="bg-[#8B1A1A]/10 max-w-[1400px] mx-auto" />

      {/* Şef */}
      <section className={`py-20 md:py-32 ${bg2}`} data-testid="interior-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="order-2 lg:order-1">
              <span className="font-body text-xs uppercase tracking-[0.3em] text-[#8B1A1A] block mb-4">{t.chef.label}</span>
              <h2 className={`font-heading text-3xl sm:text-4xl lg:text-5xl ${textH} mb-6 leading-tight`}>
                {t.chef.title1} <br /><span className="italic text-[#8B1A1A]">{t.chef.title2}</span>
              </h2>
              <p className={`font-body text-base ${textP} leading-relaxed mb-6`}>
                {t.chef.desc1}
              </p>
              <p className={`font-body text-base ${textP} leading-relaxed`}>
                {t.chef.desc2}
              </p>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} className="order-1 lg:order-2 relative">
              <img src={imgs.interior} alt={t.chef.label} className="w-full aspect-[4/3] object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Video */}
      <section className={`py-20 md:py-32 ${bg}`} data-testid="video-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <span className="font-body text-xs uppercase tracking-[0.3em] text-[#8B1A1A] block mb-3">{t.video.label}</span>
            <h2 className={`font-heading text-3xl sm:text-4xl lg:text-5xl ${textH}`}>
              {t.video.title1} <span className="italic text-[#8B1A1A]">{t.video.title2}</span>
            </h2>
            <p className={`font-body text-base ${textM} mt-4 max-w-lg mx-auto`}>{t.video.desc}</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {videoSources.map((src, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} data-testid={`video-${i}`} className={`relative aspect-video border ${borderS} overflow-hidden bg-black`}>
                <video
                  src={src}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Separator className="bg-[#8B1A1A]/10 max-w-[1400px] mx-auto" />

      {/* Google Yorumları */}
      <section className={`py-20 md:py-32 ${bg2}`} data-testid="reviews-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-6">
            <span className="font-body text-xs uppercase tracking-[0.3em] text-[#8B1A1A] block mb-3">{t.reviews.label}</span>
            <h2 className={`font-heading text-3xl sm:text-4xl lg:text-5xl ${textH}`}>
              {t.reviews.title1} <span className="italic text-[#8B1A1A]">{t.reviews.title2}</span>
            </h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-16">
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map((s) => <Star key={s} className="w-5 h-5 text-yellow-500 fill-yellow-500" />)}
            </div>
            <div className="flex items-center gap-1.5">
              <span className={`font-heading text-2xl ${textH}`}>4.8</span>
              <span className={`font-body text-sm ${textM}`}>/ 5.0</span>
            </div>
            <span className={`font-body text-xs ${textM}`}>({t.reviews.googleRating} &bull; 320+ {t.reviews.basedOn})</span>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {fakeReviews.map((review, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} data-testid={`review-card-${i}`} className={`border ${borderS} p-6 ${cardBg} hover:border-[#8B1A1A]/20 transition-colors duration-500 relative`}>
                <Quote className="w-8 h-8 text-[#8B1A1A]/10 absolute top-4 right-4" />
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#8B1A1A] flex items-center justify-center text-white font-heading text-sm">
                    {review.avatar}
                  </div>
                  <div>
                    <p className={`font-heading text-sm ${textH}`}>{review.name}</p>
                    <div className="flex items-center gap-1">
                      {Array(review.rating).fill(null).map((_, s) => <Star key={`filled-${s}`} className="w-3 h-3 text-yellow-500 fill-yellow-500" />)}
                      {Array(5 - review.rating).fill(null).map((_, s) => <Star key={`empty-${s}`} className={`w-3 h-3 ${dk ? "text-[#444]" : "text-[#ccc]"}`} />)}
                    </div>
                  </div>
                </div>
                <p className={`font-body text-sm ${textP} leading-relaxed mb-3`}>{review.text}</p>
                <p className={`font-body text-xs ${textM}`}>{review.time} {t.reviews.timeAgo}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Separator className="bg-[#8B1A1A]/10 max-w-[1400px] mx-auto" />

      {/* Harita */}
      <section className={`py-20 md:py-32 ${bg2}`} data-testid="locations-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <span className="font-body text-xs uppercase tracking-[0.3em] text-[#8B1A1A] block mb-3">{t.locations.label}</span>
            <h2 className={`font-heading text-3xl sm:text-4xl lg:text-5xl ${textH}`}>
              {t.locations.title1} <span className="italic text-[#8B1A1A]">{t.locations.title2}</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} data-testid="location-zeytinburnu" className={`border ${borderS} hover:border-[#8B1A1A]/30 transition-colors duration-500 overflow-hidden ${cardBg}`}>
              <div className="aspect-video w-full">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3010!2d28.9044!3d40.9917!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cabb027147e5bf%3A0xada76f7f93a0a0b8!2sTepsi%20Et%20Zeytinburnu!5e0!3m2!1str!2str" width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" title="Zeytinburnu" />
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl text-[#8B1A1A] mb-3">{t.locations.zeytin}</h3>
                <div className="space-y-2">
                  <div className={`flex items-start gap-2 font-body text-sm ${textP}`}><MapPin className="w-4 h-4 text-[#8B1A1A] mt-0.5 shrink-0" /><span>Seyitnizam, Mevlana Cd. No:81-83/D, 34015 Zeytinburnu/İstanbul</span></div>
                  <div className={`flex items-center gap-2 font-body text-sm ${textP}`}><Phone className="w-4 h-4 text-[#8B1A1A] shrink-0" /><span>0(553) 889 01 46</span></div>
                  <p className={`font-body text-xs ${textM} uppercase tracking-wider mt-2`}>{t.locations.noAlcohol}</p>
                </div>
              </div>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} data-testid="location-caddebostan" className={`border ${borderS} hover:border-[#8B1A1A]/30 transition-colors duration-500 overflow-hidden ${cardBg}`}>
              <div className="aspect-video w-full">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3013!2d29.066!3d40.963!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cac7ae314ec0e5%3A0x69f1ca76673b2f3c!2sTepsi%20Et%20Cadde!5e0!3m2!1str!2str" width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" title="Caddebostan" />
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl text-[#8B1A1A] mb-3">{t.locations.cadde}</h3>
                <div className="space-y-2">
                  <div className={`flex items-start gap-2 font-body text-sm ${textP}`}><MapPin className="w-4 h-4 text-[#8B1A1A] mt-0.5 shrink-0" /><span>Caddebostan, Yener Sk. No:5 D:1a, 34728 Kadıköy/İstanbul</span></div>
                  <div className={`flex items-center gap-2 font-body text-sm ${textP}`}><Phone className="w-4 h-4 text-[#8B1A1A] shrink-0" /><span>0(506) 382 46 46</span></div>
                  <p className="font-body text-xs text-[#8B1A1A] uppercase tracking-wider mt-2">{t.locations.alcohol}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Separator className="bg-[#8B1A1A]/10 max-w-[1400px] mx-auto" />

      {/* Franchise CTA */}
      <section className={`py-20 md:py-32 ${bg}`} data-testid="franchise-cta-section">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <Flame className="w-8 h-8 text-[#8B1A1A] mx-auto mb-6" />
            <h2 className={`font-heading text-3xl sm:text-4xl lg:text-5xl ${textH} mb-6`}>
              {t.franchiseCta.title1} <span className="italic text-[#8B1A1A]">{t.franchiseCta.title2}</span>
            </h2>
            <p className={`font-body text-base ${textM} mb-10 leading-relaxed`}>
              {t.franchiseCta.desc}
            </p>
            <Link to="/franchise" data-testid="franchise-cta-btn" className="inline-flex items-center gap-2 border border-[#8B1A1A] px-8 py-3.5 text-sm font-body uppercase tracking-[0.2em] text-[#8B1A1A] hover:bg-[#8B1A1A] hover:text-white transition-all duration-300">
              {t.franchiseCta.btn} <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* İletişim CTA */}
      <section className={`py-20 md:py-32 ${bg2}`} data-testid="cta-section">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 className={`font-heading text-3xl sm:text-4xl lg:text-5xl ${textH} mb-6`}>
              {t.cta.title1} <span className="italic text-[#8B1A1A]">{t.cta.title2}</span>
            </h2>
            <p className={`font-body text-base ${textM} mb-10 leading-relaxed`}>
              {t.cta.desc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+905538890146" data-testid="cta-call-zeytinburnu" className="inline-flex items-center justify-center gap-2 border border-[#8B1A1A] px-8 py-3.5 text-sm font-body uppercase tracking-[0.2em] text-[#8B1A1A] hover:bg-[#8B1A1A] hover:text-white transition-all duration-300">{t.cta.callZeytin}</a>
              <a href="tel:+905063824646" data-testid="cta-call-caddebostan" className={`inline-flex items-center justify-center gap-2 border ${borderS} px-8 py-3.5 text-sm font-body uppercase tracking-[0.2em] ${textP} hover:border-[#8B1A1A] hover:text-[#8B1A1A] transition-all duration-300`}>{t.cta.callCadde}</a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
